/**
 * Scrudix Ops Assistant — real-time team telemetry copilot.
 *
 * Full redesign: live telemetry strip, animated message entrances,
 * staged "thinking" state, structured ops answers with quick actions,
 * persistent sessions and a polished history view.
 * Self-contained styling (aiAssistant.css).
 */

import React, { useState, useRef, useEffect } from 'react';
import { AnimatePresence, motion } from 'motion/react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {
  Activity,
  ArrowUp,
  Clock3,
  Flame,
  History,
  Plus,
  ShieldCheck,
  Sparkles,
  Trash2,
  TrendingUp,
  Users,
  X,
  Zap,
} from 'lucide-react';
import { useTeamDashboard } from '../TeamDashboardContext';
import apiClient from '../../../../services/apiClient';
import './aiAssistant.css';

interface Message {
  role: 'user' | 'assistant';
  content: string;
  timestamp: string;
}

interface ChatSession {
  id: string;
  title: string;
  messages: Message[];
  timestamp: string;
}

interface AiAssistantModalProps {
  onClose: () => void;
  dashboardData: {
    hoursLogged: number;
    spent: number;
    remainingBudget: number;
    totalBudget: number;
    activeMembers: number;
    activeProjects: number;
    qualityAvg: number;
  };
}

const T = {
  green: '#34d399',
  amber: '#fbbf24',
  red: '#fb4d6d',
  cyan: '#22d3ee',
  accent: '#6e6bff',
  accent2: '#a855f7',
};

const FONT_ID = 'opsx-fonts';
function ensureFonts() {
  if (document.getElementById(FONT_ID)) return;
  const link = document.createElement('link');
  link.id = FONT_ID;
  link.rel = 'stylesheet';
  link.href =
    'https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&family=JetBrains+Mono:wght@400;500;600;700&display=swap';
  document.head.appendChild(link);
}

const OpsMarkdown: React.FC<{ content: string }> = ({ content }) => (
  <div className="opsx-md">
    <ReactMarkdown remarkPlugins={[remarkGfm]}>{content}</ReactMarkdown>
  </div>
);

/** Streams plain text, then swaps to full markdown — smooth + cheap. */
const RevealMessage: React.FC<{ content: string; onDone: () => void }> = ({ content, onDone }) => {
  const [shown, setShown] = useState(0);
  const [done, setDone] = useState(false);

  useEffect(() => {
    const step = Math.max(2, Math.round(content.length / 200));
    const timer = setInterval(() => {
      setShown((prev) => {
        const next = prev + step;
        if (next >= content.length) {
          clearInterval(timer);
          setDone(true);
          onDone();
          return content.length;
        }
        return next;
      });
    }, 16);
    return () => clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [content]);

  if (done) return <OpsMarkdown content={content} />;
  return (
    <span style={{ whiteSpace: 'pre-wrap' }}>
      {content.slice(0, shown)}
      <span style={{ color: 'var(--opsx-accent)', animation: 'opsxDots 1s steps(2) infinite' }}>▍</span>
    </span>
  );
};

const QUICK_ACTIONS = [
  { label: 'Burn rate risk', icon: Flame, prompt: 'Analyze our budget burn rate risk. Are we sustainable this sprint? Give me the verdict and the exact numbers.' },
  { label: 'Quality audit', icon: ShieldCheck, prompt: 'Audit team sprint quality scores. Who needs review support and why?' },
  { label: 'Velocity tips', icon: Zap, prompt: 'Suggest velocity optimization tips based on our current hours, commits and project load.' },
  { label: 'Team pulse', icon: Activity, prompt: 'Give me a team pulse summary: who is shipping, who is quiet, and where the risk is.' },
];

export default function AiAssistantModal({ onClose, dashboardData }: AiAssistantModalProps) {
  const { initialPrompt, setInitialPrompt, theme } = useTeamDashboard();
  const [sessions, setSessions] = useState<ChatSession[]>(() => {
    try {
      const raw = localStorage.getItem('scrudix_ops_chat_sessions');
      return raw ? JSON.parse(raw) : [];
    } catch {
      return [];
    }
  });
  const [activeSessionId, setActiveSessionId] = useState<string | null>(null);
  const [showHistory, setShowHistory] = useState(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [revealing, setRevealing] = useState(false);
  const [thinkStage, setThinkStage] = useState(0);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    ensureFonts();
  }, []);

  useEffect(() => {
    if (initialPrompt) {
      setInput(initialPrompt);
      setInitialPrompt(null);
      setTimeout(() => inputRef.current?.focus(), 200);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialPrompt]);

  /* welcome message */
  useEffect(() => {
    if (!activeSessionId) {
      setMessages([
        {
          role: 'assistant',
          content: '__welcome__',
          timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
        },
      ]);
      setTimeout(() => inputRef.current?.focus(), 250);
    } else {
      const activeSession = sessions.find((s) => s.id === activeSessionId);
      if (activeSession) setMessages(activeSession.messages);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSessionId]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, loading]);

  /* staged thinking labels */
  useEffect(() => {
    if (!loading) return;
    setThinkStage(0);
    const t1 = setTimeout(() => setThinkStage(1), 900);
    const t2 = setTimeout(() => setThinkStage(2), 1900);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
    };
  }, [loading]);

  const persistSessions = (next: ChatSession[]) => {
    setSessions(next);
    localStorage.setItem('scrudix_ops_chat_sessions', JSON.stringify(next));
  };

  const handleSend = async (textToSend?: string) => {
    const promptText = (textToSend || input).trim();
    if (!promptText || loading) return;

    const userMessage: Message = {
      role: 'user',
      content: promptText,
      timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
    };

    const newMessages = [...messages, userMessage];
    setMessages(newMessages);
    setInput('');
    setLoading(true);

    let updatedSessions = [...sessions];
    let currentSessionId = activeSessionId;
    if (!currentSessionId) {
      currentSessionId = crypto.randomUUID();
      updatedSessions.unshift({
        id: currentSessionId,
        title: promptText.length > 34 ? `${promptText.slice(0, 34)}…` : promptText,
        messages: newMessages,
        timestamp: `${new Date().toLocaleDateString([], { month: 'short', day: 'numeric' })} ${new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`,
      });
      setActiveSessionId(currentSessionId);
    } else {
      updatedSessions = updatedSessions.map((s) => (s.id === currentSessionId ? { ...s, messages: newMessages } : s));
    }
    persistSessions(updatedSessions);

    const systemPrompt = `You are the Scrudix Ops Assistant — a senior product-operations director with live team telemetry. Be sharp, factual and action-oriented.

LIVE TELEMETRY:
- Hours logged this week: ${dashboardData.hoursLogged} hrs
- Budget: ₹${dashboardData.spent.toLocaleString()} spent of ₹${dashboardData.totalBudget.toLocaleString()} (₹${dashboardData.remainingBudget.toLocaleString()} remaining)
- Active members: ${dashboardData.activeMembers} · Active projects: ${dashboardData.activeProjects}
- Code quality sprint avg: ${dashboardData.qualityAvg}/100

RESPONSE FORMAT (strict):
- ONE bold verdict sentence first.
- ## Signal — 2-3 short bullets grounded in the telemetry above (use exact numbers).
- ## Action — 1-3 imperative steps the team should take now.
- Keep the whole answer under 140 words. No emojis, no disclaimers. If a metric is risky (fast burn, low quality, idle members), call it out by name.`;

    try {
      const res = await apiClient.post<{ response?: string }>('/ai/chat', {
        messages: newMessages.filter((m) => m.content !== '__welcome__').map((m) => ({ role: m.role, content: m.content })),
        systemPrompt,
      });
      const responseText = res.data?.response || (res as { response?: string }).response || 'I could not process that query. Please try again.';
      const assistantMessage: Message = {
        role: 'assistant',
        content: responseText,
        timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
      };
      const finalMessages = [...newMessages, assistantMessage];
      setMessages(finalMessages);
      setRevealing(true);
      persistSessions(updatedSessions.map((s) => (s.id === currentSessionId ? { ...s, messages: finalMessages } : s)));
    } catch {
      const finalMessages = [
        ...newMessages,
        {
          role: 'assistant' as const,
          content: '**Operations link degraded.** I could not reach the analysis backend — check connectivity and retry. Your telemetry is unchanged.',
          timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
        },
      ];
      setMessages(finalMessages);
      setRevealing(true);
      persistSessions(updatedSessions.map((s) => (s.id === currentSessionId ? { ...s, messages: finalMessages } : s)));
    } finally {
      setLoading(false);
    }
  };

  const deleteSession = (e: React.MouseEvent, id: string) => {
    e.stopPropagation();
    const updated = sessions.filter((s) => s.id !== id);
    persistSessions(updated);
    if (activeSessionId === id) setActiveSessionId(null);
  };

  const isDark = theme === 'dark';
  const budgetPct = dashboardData.totalBudget > 0 ? Math.round((dashboardData.remainingBudget / dashboardData.totalBudget) * 100) : 0;
  const budgetColor = budgetPct > 40 ? T.green : budgetPct > 15 ? T.amber : T.red;
  const qualityColor = dashboardData.qualityAvg >= 75 ? T.green : dashboardData.qualityAvg >= 60 ? T.amber : T.red;
  const THINK_STAGES = ['Reading live telemetry', 'Correlating risk signals', 'Drafting ops verdict'];

  return (
    <>
      <motion.div className="opsx-overlay" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={onClose} />
      <motion.div
        className="opsx"
        data-theme={isDark ? 'dark' : 'light'}
        initial={{ x: '100%' }}
        animate={{ x: 0 }}
        exit={{ x: '100%' }}
        transition={{ type: 'spring', stiffness: 300, damping: 32 }}
      >
        {/* header */}
        <div className="opsx-head">
          <span className="opsx-logo">
            <Sparkles size={17} />
          </span>
          <div>
            <div className="opsx-title">Scrudix Ops Assistant</div>
            <div className="opsx-sub">
              <span className="opsx-live" />
              Telemetry · live
            </div>
          </div>
          <div className="opsx-head-actions">
            <button
              className="opsx-icon-btn"
              title="New chat"
              onClick={() => {
                setActiveSessionId(null);
                setShowHistory(false);
              }}
            >
              <Plus size={15} />
            </button>
            <button className={`opsx-icon-btn ${showHistory ? 'opsx-on' : ''}`} title="History" onClick={() => setShowHistory((v) => !v)}>
              <History size={14} />
            </button>
            <button className="opsx-icon-btn" title="Close" onClick={onClose}>
              <X size={15} />
            </button>
          </div>
        </div>

        {/* telemetry strip */}
        <div className="opsx-telemetry">
          {[
            { label: 'Hours', value: `${dashboardData.hoursLogged}h`, color: T.accent, icon: Clock3 },
            { label: 'Budget safe', value: `${budgetPct}%`, color: budgetColor, icon: TrendingUp },
            { label: 'Quality', value: `${dashboardData.qualityAvg}`, color: qualityColor, icon: ShieldCheck },
          ].map((s) => (
            <div key={s.label} className="opsx-stat">
              <div className="opsx-stat-label" style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                <s.icon size={8} style={{ color: s.color }} />
                {s.label}
              </div>
              <div className="opsx-stat-val" style={{ color: s.color }}>
                {s.value}
              </div>
            </div>
          ))}
        </div>

        {showHistory ? (
          /* ── history ── */
          <div className="opsx-body">
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
              <span className="opsx-stat-label">Saved conversations</span>
              {sessions.length > 0 && (
                <button
                  onClick={() => {
                    persistSessions([]);
                    setActiveSessionId(null);
                  }}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 10, fontWeight: 700, color: 'var(--opsx-red)', fontFamily: 'inherit' }}
                >
                  Clear all
                </button>
              )}
            </div>
            {sessions.length === 0 && (
              <div style={{ textAlign: 'center', padding: '44px 20px' }}>
                <History size={22} style={{ color: 'var(--opsx-faint)', marginBottom: 10 }} />
                <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--opsx-dim)' }}>No saved conversations</div>
                <div style={{ fontSize: 10.5, color: 'var(--opsx-faint)', marginTop: 4 }}>Start a chat — sessions save automatically.</div>
              </div>
            )}
            {sessions.map((s) => (
              <button
                key={s.id}
                className={`opsx-history-item ${activeSessionId === s.id ? 'opsx-on' : ''}`}
                onClick={() => {
                  setActiveSessionId(s.id);
                  setShowHistory(false);
                }}
              >
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div className="opsx-history-title">{s.title}</div>
                  <div className="opsx-history-meta">
                    {s.timestamp} · {s.messages.length} messages
                  </div>
                </div>
                <span
                  className="opsx-icon-btn"
                  role="button"
                  title="Delete"
                  onClick={(e) => deleteSession(e, s.id)}
                >
                  <Trash2 size={12} />
                </span>
              </button>
            ))}
          </div>
        ) : (
          /* ── chat ── */
          <>
            <div className="opsx-body">
              {messages.map((msg, i) => {
                const isLast = i === messages.length - 1;
                const isWelcome = msg.content === '__welcome__';
                return (
                  <motion.div
                    key={`${i}_${msg.timestamp}`}
                    className={`opsx-msg ${msg.role === 'user' ? 'opsx-msg-user' : 'opsx-msg-assistant'}`}
                    initial={{ opacity: 0, y: 12, scale: 0.98 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                  >
                    {msg.role === 'assistant' && <span className="opsx-avatar">S</span>}
                    <div className="opsx-bubble">
                      {isWelcome ? (
                        <div>
                          <div style={{ fontSize: 13, fontWeight: 800, color: 'var(--opsx-text)', marginBottom: 4 }}>
                            Ops copilot online.
                          </div>
                          <div style={{ fontSize: 11.5, color: 'var(--opsx-dim)', lineHeight: 1.55 }}>
                            I have already analyzed this week's team telemetry. Ask me for burn risk, quality audits, or velocity calls — or pick an action below.
                          </div>
                          <div className="opsx-welcome-grid">
                            <div className="opsx-welcome-cell">
                              <div className="opsx-stat-label">Hours logged</div>
                              <div className="opsx-stat-val">{dashboardData.hoursLogged} hrs</div>
                            </div>
                            <div className="opsx-welcome-cell">
                              <div className="opsx-stat-label">Quality avg</div>
                              <div className="opsx-stat-val" style={{ color: qualityColor }}>
                                {dashboardData.qualityAvg}/100
                              </div>
                            </div>
                            <div className="opsx-welcome-cell opsx-welcome-cell-wide">
                              <div className="opsx-stat-label">Remaining budget</div>
                              <div className="opsx-stat-val" style={{ color: budgetColor }}>
                                ₹{dashboardData.remainingBudget.toLocaleString()}
                                <span style={{ fontSize: 9, color: 'var(--opsx-faint)', fontWeight: 500 }}> of ₹{dashboardData.totalBudget.toLocaleString()}</span>
                              </div>
                            </div>
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 10, color: 'var(--opsx-faint)' }}>
                            <Users size={10} />
                            {dashboardData.activeMembers} members · {dashboardData.activeProjects} active projects
                          </div>
                        </div>
                      ) : msg.role === 'assistant' ? (
                        isLast && revealing ? (
                          <RevealMessage content={msg.content} onDone={() => setRevealing(false)} />
                        ) : (
                          <OpsMarkdown content={msg.content} />
                        )
                      ) : (
                        <div style={{ whiteSpace: 'pre-wrap' }}>{msg.content}</div>
                      )}
                      <div className="opsx-time">{msg.timestamp}</div>
                    </div>
                  </motion.div>
                );
              })}

              {loading && (
                <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="opsx-msg opsx-msg-assistant">
                  <span className="opsx-avatar">S</span>
                  <div className="opsx-thinking">
                    <span className="opsx-thinking-dot" />
                    <span className="opsx-thinking-text">{THINK_STAGES[thinkStage]}</span>
                  </div>
                </motion.div>
              )}
              <div ref={messagesEndRef} />
            </div>

            {/* footer */}
            <div className="opsx-foot">
              <div className="opsx-chips">
                {QUICK_ACTIONS.map((a) => (
                  <button key={a.label} className="opsx-chip" onClick={() => void handleSend(a.prompt)} disabled={loading}>
                    <a.icon size={10} />
                    {a.label}
                  </button>
                ))}
              </div>
              <div className="opsx-input-row">
                <textarea
                  ref={inputRef}
                  className="opsx-input"
                  rows={1}
                  value={input}
                  placeholder="Ask Ops Assistant…"
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      void handleSend();
                    }
                  }}
                />
                <button className="opsx-send" onClick={() => void handleSend()} disabled={loading || !input.trim()} aria-label="Send">
                  <ArrowUp size={15} strokeWidth={2.5} />
                </button>
              </div>
            </div>
          </>
        )}
      </motion.div>
    </>
  );
}
